import cloneDeep from 'lodash/cloneDeep';
import { CSSObject } from 'styled-components';

const borderRadius = {
  default: '5px',
  sm: '5px',
  md: '10px',
  lg: '20px',
};

const color = {
  logo: '#E54500',
  text: '#330F00',
  hr: '#d9d9d9',
  cardInfo: '#7b7070',
  brandOrange: '#ffb021',
  brandRed: '#f93919',
  muted: '#b5b5b5',
  halfMuted: '#777777',
  fake: '#f6f6f6',
  contentBackground: '#ffffff',
  headerBackground: '#ffffff',
  highlightedBackground: '#f5f7f9',
  footerBackground: 'transparent',
  link: '#ffb021', // brand orange
  error: '#f93919', // brand red

  blue: '#1c569d',
  accent: '#ffb021', // brand orange
};

const shadow = {
  header: '0 0 22px 5px rgba(124, 80, 0, 0.12)',
  default: '0 0 8px rgba(0, 0, 0, 0.03), 0 10px 15px rgba(0, 0, 0, 0.1)',
};

const buttonBackgrounds = {
  cta: `linear-gradient(252deg, ${color.brandOrange}, ${color.brandRed})`,
  primary: color.brandOrange,
  secondary: '#fff',
  disabled: color.hr,
  muted: '#f3f3f3',
  hover: `linear-gradient(66deg, ${color.brandOrange}, ${color.brandRed})`,
  click: `linear-gradient(33deg, ${color.brandOrange}, ${color.brandRed})`,
  light: `linear-gradient(66deg, rgba(${color.brandOrange}, 0.32), rgba(${color.brandRed}, 0.32))`,
};

const buttonBorders = {
  hover: color.brandOrange,
  focus: color.brandRed,
  primary: color.brandOrange,
  secondary: color.brandOrange,
  disabled: color.hr,
};

const buttonColors = {
  primary: color.brandOrange,
  disabled: color.hr,
  muted: '#f3f3f3',
};

const agenda = {
  color: {
    outline: '#d2d2d2',
    canvas: '#ffffff',
    buttonDefault: '#d2d2d2',
    buttonPrimary: color.brandOrange,
  },
};

const formColors = {
  border: color.hr,
  borderFocus: color.halfMuted,
  placeholder: color.muted,
};

const counterColor = {
  background: color.logo,
  color: '#fff',
};

const headerHeightDesktop = '80px';

const header = {
  heightDesktop: headerHeightDesktop,
};

const fontsUrl =
  'https://fonts.googleapis.com/css?family=Nunito+Sans:400,700|Quicksand:700|Caveat:400,700&display=swap&subset=latin-ext';

const logoFontUrl = 'https://fonts.googleapis.com/css?family=Nunito:600&display=block&text=introwise';

const quicksandFont = `'Quicksand', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif`;
const nunitoFont = `'Nunito Sans', Arial, sans-serif`;
const logoFont = `Nunito, Arial, sans-serif`;

const caveatFont = `'Caveat', cursive, sans-serif`;

const typography: { [index: string]: CSSObject } = {
  regular: {
    fontFamily: nunitoFont,
    fontSize: 16,
    letterSpacing: '1%',
    lineHeight: 1.62,
    fontWeight: 400,
  },
  small: {
    fontSize: '0.75em',
  },
  strong: {
    fontWeight: 'bolder',
  },
  hero: {
    fontFamily: quicksandFont,
    fontWeight: 700,
    lineHeight: 1.2,
    fontSize: 56,
  },
  h1: {
    fontFamily: quicksandFont,
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: 26,
    lineHeight: 1.52,
    fontWeight: 400,
  },
  h3: {
    fontSize: 20,
    fontWeight: 700,
  },
  h4: {
    fontFamily: quicksandFont,
    fontSize: 16,
    fontWeight: 700,
  },
  cursive: {
    fontFamily: caveatFont,
  },
};

const fakeDefault: CSSObject = {
  color: 'transparent',
  background: color.fake,
  borderRadius: borderRadius.sm,
};

const fake = {
  default: fakeDefault,
  loading: fakeDefault,
  error: {
    color: 'transparent',
    backgroundColor: '#fff',
    borderRadius: borderRadius.default,
    backgroundImage: `url(
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23e2d202' fill-opacity='1'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 1.4l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM38.59 40l-2.83-2.83 1.41-1.41L40 38.59V40h-1.41zM40 1.41l-2.83 2.83-1.41-1.41L38.59 0H40v1.41zM20 18.6l2.83-2.83 1.41 1.41L21.41 20l2.83 2.83-1.41 1.41L20 21.41l-2.83 2.83-1.41-1.41L18.59 20l-2.83-2.83 1.41-1.41L20 18.59z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"
    )`,
    border: `2px dashed #e2d202`,
  } as CSSObject,
  notFound: {
    color: 'transparent',
    backgroundColor: color.fake,
    borderRadius: borderRadius.default,
    backgroundImage: `url(
      "data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E"
    )`,
    border: `1px dashed ${color.muted}`,
  } as CSSObject,
};

const baseTheme = {
  borderRadius,
  color,
  button: {
    transition: 'none',
    color: buttonColors,
    background: buttonBackgrounds,
    borderColor: buttonBorders,
    opacity: {
      hover: 0.8,
    },
    font: {
      fontFamily: nunitoFont,
      fontWeight: 700,
    } as CSSObject,
  },
  form: {
    color: formColors,
  },
  header,
  logo: {
    color: color.logo,
    font: {
      fontFamily: logoFont,
      fontWeight: 600,
    } as CSSObject,
  },
  agenda,
  shadow,
  fontsUrl,
  logoFontUrl,
  typography,
  counterColor,
  fake,
};

export type ThemeType = typeof baseTheme;

const Main: ThemeType = baseTheme;

const Experimental: ThemeType = cloneDeep(Main);

Experimental.borderRadius.default = '12px';
Experimental.borderRadius.sm = '6px';
Experimental.borderRadius.md = '12px';
Experimental.borderRadius.lg = '24px';
Experimental.shadow.default = '0px 0px 20px rgba(37, 38, 94, 0.1)';
Experimental.color.link = color.blue;
Experimental.color.accent = color.blue;
Experimental.button.transition = 'background-color ease 0.15s';
Experimental.button.background.primary = color.blue;
Experimental.button.background.hover = '#0073FF';
Experimental.button.background.click = '#043168';
Experimental.button.borderColor.hover = '#0073FF';
Experimental.button.borderColor.focus = '#043168';
Experimental.button.borderColor.primary = color.blue;
Experimental.button.borderColor.secondary = color.blue;
Experimental.button.color.primary = color.blue;

const defaultTheme = Main;

export { Main, Experimental };
export default defaultTheme;
